import { useState } from "react";
import { Link } from "react-router-dom";

import {
  type OptionItem,
  OptionType,
  Status,
  useGetOptionsQuery,
} from "@/api/admin";
import { type IBrandInfo, useGetAllBrandsQuery } from "@/api/admin/brands";
import { AdminSections } from "@/api/admin/teamRoles";
import {
  Loader,
  TableCellBadge,
  TableCellLink,
  TableCellText,
  TableHeadCellCustom,
  TableHeadCellSort,
  type TableHeaderType,
} from "@/components/atoms";
import {
  DropDownViewMenu,
  FilterSearch,
  type StatusVariants,
  type SubMenuConfig,
} from "@/components/molecules";
import type { SubMenuItem } from "@/components/molecules/FilterSearch/DropDownFilterMenu";
import { PATHS } from "@/config/path";
import { usePermission } from "@/services/casl/usePermission";
import { formatIsoDate } from "@/utils/formatIsoToUTC";
import { useParamsFromUrl } from "@/utils/hooks/useParamsFromUrl";
import { type SortType, useTableParams } from "@/utils/hooks/useTableParams";
import { useVisibleFlags } from "@/utils/hooks/useVisibleFlags";

import DynamicTable from "../DynamicTable";
import { type ColumnConfig, isOptionItem } from "../type";

enum AccountBrandsColumn {
  BRAND_NAME = "Brand name",
  REFERENCE_NUMBER = "Reference number",
  DATE_TIME = "Created at",
  LEGAL_ENTITY = "Legal entity",
  EMAIL = "Email",
  COUNTRY = "Country",
  ACTIVITY = "Status",
  BRANCHES_UNDER_BRAND = "Branches under brand",
}

const TABLE_HEADERS: TableHeaderType = Object.values(AccountBrandsColumn);

const AccountBrandsTable = () => {
  const hasPermissionBrand = usePermission(AdminSections.BRAND);
  const hasPermissionBranchList = usePermission(AdminSections.BRANCHES_LIST);
  const [searchValue, setSearchValue] = useState("");

  const { tableParams, handleNext, handlePrev, setTableParams } =
    useTableParams(
      {
        initialParams: { query: "" },
      },
      searchValue
    );

  useParamsFromUrl(setTableParams, ["legalEntityId", "groupId"]);

  const { data: brandData, isPending: isBrandPending } =
    useGetAllBrandsQuery(tableParams);

  const { data: optionsData, isPending: isOptionPending } = useGetOptionsQuery([
    OptionType.Countries,
    OptionType.LegalEntities,
    OptionType.Groups,
  ]);

  const countryNames = optionsData?.countries?.map(c => c.name) ?? [];

  const handleSetFilter = (
    selectedMap: Record<string, string[] | OptionItem[]>
  ) => {
    const selectedCountry = selectedMap[AccountBrandsColumn.COUNTRY]?.[0] ?? "";
    const selectedGroupName = selectedMap["Group"]?.[0] ?? "";
    const selectedActivity =
      selectedMap[AccountBrandsColumn.ACTIVITY]?.[0] ?? "";
    const selectedBranches = selectedMap["Branches"]?.[0] ?? "";
    const selectedLegalEntity =
      selectedMap[AccountBrandsColumn.LEGAL_ENTITY]?.[0];

    setTableParams(prev => ({
      ...prev,
      country:
        typeof selectedCountry === "string" ? selectedCountry : undefined,
      groupId: isOptionItem(selectedGroupName)
        ? selectedGroupName.id
        : undefined,
      legalEntityId: isOptionItem(selectedLegalEntity)
        ? selectedLegalEntity.id
        : undefined,
      active: selectedActivity
        ? selectedActivity === Status.INACTIVE
          ? false
          : true
        : undefined,
      hasBranches: selectedBranches
        ? selectedBranches === "With branches"
          ? true
          : false
        : undefined,
      offset: 0,
    }));
  };

  const handleSortChange = (direction: SortType) => {
    setTableParams(prev => ({
      ...prev,
      sortBy: direction ? "name" : undefined,
      sortDirection: direction || undefined,
      offset: 0,
    }));
  };

  const SUB_MENUS: Record<string, SubMenuItem[]> = {
    Group: optionsData?.groups ?? [],
    [AccountBrandsColumn.LEGAL_ENTITY]: optionsData?.legalEntities ?? [],
    [AccountBrandsColumn.COUNTRY]: countryNames,
    ["Status"]: [Status.ACTIVE, Status.INACTIVE],
    ["Branches"]: ["With branches", "No branches"],
  };

  const MENU_LIST = Object.keys(SUB_MENUS);

  const SUB_MENU_REGISTRY: Record<string, SubMenuConfig> = {
    Group: {
      type: "search",
      placeholder: "Search groups",
      isSingleSelect: true,
    },
    [AccountBrandsColumn.LEGAL_ENTITY]: {
      type: "search",
      placeholder: "Search legal entity",
      isSingleSelect: true,
    },
    [AccountBrandsColumn.COUNTRY]: { type: "role", isSingleSelect: true },
    ["Status"]: { type: "status", isSingleSelect: true },
    ["Branches"]: { type: "role", isSingleSelect: true },
  };

  const STATUS_VARIANT_MAP: StatusVariants = {
    Active: { badge: "success", dot: "#65A30D" },
  };

  const { visibleColumns, setVisibleColumns, flags } =
    useVisibleFlags(TABLE_HEADERS);

  const columns: ColumnConfig<IBrandInfo>[] = [
    {
      key: "name",
      label: AccountBrandsColumn.BRAND_NAME,
      visible: flags[AccountBrandsColumn.BRAND_NAME],
      headerComponent: ({ label }) => (
        <TableHeadCellSort
          sortOrder={tableParams.sortDirection}
          onSortChange={handleSortChange}
          field="name"
          activeField={tableParams.sortBy}
        >
          {label}
        </TableHeadCellSort>
      ),

      cellRenderer: (row, isLast) => {
        return hasPermissionBrand ? (
          <TableCellLink last={isLast}>
            <Link
              to={PATHS.ADMIN.ACCOUNT_BRAND_DETAILS(row.id?.toString() || "")}
              data-testid={`account-brands-cell-id-${row.id}`}
            >
              {row.name}
            </Link>
          </TableCellLink>
        ) : (
          <TableCellText last={isLast}>{row.name}</TableCellText>
        );
      },
    },
    {
      key: "referenceNumber",
      label: AccountBrandsColumn.REFERENCE_NUMBER,
      visible: flags[AccountBrandsColumn.REFERENCE_NUMBER],
      headerComponent: ({ label }) => (
        <TableHeadCellCustom>{label}</TableHeadCellCustom>
      ),
      cellRenderer: (row, isLast) => (
        <TableCellText last={isLast}>{row.referenceNumber}</TableCellText>
      ),
    },
    {
      key: "createdAt",
      label: AccountBrandsColumn.DATE_TIME,
      visible: flags[AccountBrandsColumn.DATE_TIME],
      headerComponent: ({ label }) => (
        <TableHeadCellCustom>{label}</TableHeadCellCustom>
      ),
      cellRenderer: (row, isLast) => (
        <TableCellText last={isLast}>
          {formatIsoDate(row?.createdAt) || "-"}
        </TableCellText>
      ),
    },
    {
      key: "legalEntityName",
      label: AccountBrandsColumn.LEGAL_ENTITY,
      visible: flags[AccountBrandsColumn.LEGAL_ENTITY],
      headerComponent: ({ label }) => (
        <TableHeadCellCustom>{label}</TableHeadCellCustom>
      ),
      cellRenderer: (row, isLast) => (
        <TableCellText last={isLast}>
          {row.legalEntityName || "-"}
        </TableCellText>
      ),
    },
    {
      key: "email",
      label: AccountBrandsColumn.EMAIL,
      visible: flags[AccountBrandsColumn.EMAIL],
      headerComponent: ({ label }) => (
        <TableHeadCellCustom>{label}</TableHeadCellCustom>
      ),
      cellRenderer: (row, isLast) => (
        <TableCellText last={isLast}>{row.email ?? "-"}</TableCellText>
      ),
    },
    {
      key: "country",
      label: AccountBrandsColumn.COUNTRY,
      visible: flags[AccountBrandsColumn.COUNTRY],
      headerComponent: ({ label }) => (
        <TableHeadCellCustom>{label}</TableHeadCellCustom>
      ),
      cellRenderer: (row, isLast) => (
        <TableCellText last={isLast}>{row.country}</TableCellText>
      ),
    },
    {
      key: "active",
      label: AccountBrandsColumn.ACTIVITY,
      visible: flags[AccountBrandsColumn.ACTIVITY],
      headerComponent: ({ label }) => (
        <TableHeadCellCustom>{label}</TableHeadCellCustom>
      ),
      cellRenderer: (row, isLast) => (
        <TableCellBadge
          last={isLast}
          stage={row.active ? "success" : "default"}
        >
          {row.active ? Status.ACTIVE : Status.INACTIVE}
        </TableCellBadge>
      ),
    },
    {
      key: "branchesCount",
      label: AccountBrandsColumn.BRANCHES_UNDER_BRAND,
      visible: flags[AccountBrandsColumn.BRANCHES_UNDER_BRAND],
      headerComponent: ({ label }) => (
        <TableHeadCellCustom>{label}</TableHeadCellCustom>
      ),
      cellRenderer: (row, isLast) => {
        return hasPermissionBranchList && row?.branchesCount ? (
          <TableCellLink
            classes="w-[14%]"
            last={isLast}
            data-testid={`account-brands-cell-branches-${row.branchesCount}`}
          >
            <Link
              to={`${PATHS.ADMIN.ACCOUNT}?offset=0&limit=25&brandId=${row.id}#branches`}
              data-testid={`account-brands-cell-branches-link-${row.branchesCount}`}
            >
              {row.branchesCount}
            </Link>
          </TableCellLink>
        ) : (
          <TableCellText last={isLast}>
            {row.branchesCount || "-"}
          </TableCellText>
        );
      },
    },
  ];

  return (
    <>
      <FilterSearch
        menuList={MENU_LIST}
        subMenus={SUB_MENUS}
        subMenuRegistry={SUB_MENU_REGISTRY}
        statusVariant={STATUS_VARIANT_MAP}
        searchValue={searchValue}
        setSearchValue={setSearchValue}
        handleSetFilter={handleSetFilter}
      >
        <DropDownViewMenu
          viewList={TABLE_HEADERS}
          visibleColumns={visibleColumns}
          setVisibleColumns={setVisibleColumns}
        />
      </FilterSearch>

      {isBrandPending || isOptionPending ? (
        <Loader />
      ) : (
        <DynamicTable<IBrandInfo>
          data={brandData?.items || []}
          columns={columns}
          searchValue={searchValue}
          next={handleNext}
          prev={handlePrev}
          tableParams={brandData}
          setTableParams={setTableParams}
        />
      )}
    </>
  );
};

export default AccountBrandsTable;
